import type { NodeMouseHandler } from '@xyflow/react';
import { useCallback, type KeyboardEvent } from 'react';

import type { StructureEntityFlowNode } from '../canvas';

// A click, or Enter or Space on a focused card, opens the entity. External entities are there for context only.
function useEntityActivation(
  flowNodes: ReadonlyArray<StructureEntityFlowNode>,
  onEntityClick?: (entityId: number) => void,
  onUnlinkedClick?: (entityId: number) => void
) {
  const activate = useCallback(
    (node: Readonly<StructureEntityFlowNode>) => {
      if (node.data.entity.isExternal) return;
      const onClick = node.data.isUnlinked
        ? (onUnlinkedClick ?? onEntityClick)
        : onEntityClick;
      onClick?.(node.data.entity.id);
    },
    [onEntityClick, onUnlinkedClick]
  );

  const handleNodeClick = useCallback<
    NodeMouseHandler<StructureEntityFlowNode>
  >((_event, node) => activate(node), [activate]);

  // React Flow only selects a focused node on these keys, so the canvas opens it.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { key, target } = event;
      if (key !== 'Enter' && key !== ' ') return;
      if (
        !(target instanceof HTMLElement) ||
        !target.classList.contains('react-flow__node')
      ) {
        return;
      }
      const node = flowNodes.find(
        (flowNode) => flowNode.id === target.dataset.id
      );
      if (!node) return;
      event.preventDefault();
      activate(node);
    },
    [flowNodes, activate]
  );

  return { handleNodeClick, handleKeyDown };
}

export { useEntityActivation };
